import { useState, useEffect, useCallback, useRef } from "react";

export const usePopupVisibility = (
  isDiscographyOrMixes,
  inactivityDuration = 4000
) => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const inactivityTimerRef = useRef(null);

  // Function to start the inactivity timer (used on home page)
  const startInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      setIsPopupVisible(true);
    }, inactivityDuration);
  }, [inactivityDuration]);

  // Activity event handler - behavior depends on page type
  const handleUserActivity = useCallback(
    (event) => {
      // Ensure target is a DOM element before using .closest
      const target = event ? event.target : null;
      if (target && target.closest && target.closest(".footer")) {
        return; // Ignore events from footer
      }

      // Close popup on activity for all pages
      setIsPopupVisible(false);

      if (isDiscographyOrMixes) {
        // On home page: also reset inactivity timer
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        startInactivityTimer();
      }
    },
    [isDiscographyOrMixes, startInactivityTimer]
  );

  const handlePopupClose = useCallback(() => {
    setIsPopupVisible(false);
    // If on home page, restart inactivity timer after closing
    if (isDiscographyOrMixes) {
      startInactivityTimer();
    }
  }, [isDiscographyOrMixes, startInactivityTimer]);

  const handlePopupOpen = useCallback(() => {
    setIsPopupVisible(true);
    // When manually opening the popup, clear any inactivity timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  }, []);

  // Set up activity tracking and initial state
  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }

    const activityEvents = ["mousemove", "keydown", "scroll", "touchstart"];

    // Start inactivity timer immediately on home page
    if (isDiscographyOrMixes) {
      startInactivityTimer();
    }

    // Set up event listeners for all pages
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity);
    });

    return () => {
      // Clean up
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [
    handleUserActivity,
    isInitialRender,
    isDiscographyOrMixes,
    startInactivityTimer,
  ]);

  return {
    isPopupVisible,
    handlePopupClose,
    handlePopupOpen,
    isInitialRender,
  };
};
